import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Calendar,
  ChevronRight,
  CircleHelp,
  Copy,
  Crown,
  Info,
  LogOut,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Users,
} from 'lucide-react';
import {
  COINS_DESC,
  COINS_PLAY_DESC,
  EARN_DESC,
  PREMIUM_OFF_BODY,
  PREMIUM_OFF_DIALOG,
  PREMIUM_OFF_TITLE,
  PREMIUM_ON_DIALOG,
  SAMPLE_ACCOUNT,
  SIGNOUT_DESC,
  SYNCED_DESC,
  accountInitials,
  deviceCountLabel,
  isGoogleLogin,
  safeCount,
  signInShort,
  signInTitle,
  type AccountSnapshot,
} from '../../data/account-mock';
import { AccountArt, CoinsArt, PremiumArt } from './AccountArt';
import './account.css';

const PREVIEW_MS = 680;

export interface AccountViewProps {
  fullName: string;
  email: string;
  phone: string;
  loginType: string;
  playerId: string;
  /**
   * Leave unset to use the built-in sample plan.
   * Pass the account API payload once it exists. Keep the object reference stable.
   */
  account?: AccountSnapshot;
  /**
   * Leave unset to preview skeletons once.
   * Pass a real flag once account APIs are wired.
   */
  loading?: boolean;
  onSignOut: () => void;
}

type AccountDialog = 'plan' | 'coins' | 'earn' | 'signout';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
}

function usePreviewLoading(enabled: boolean): boolean {
  const [loading, setLoading] = useState(() => enabled && !prefersReducedMotion());

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = window.setTimeout(() => setLoading(false), PREVIEW_MS);
    return () => window.clearTimeout(id);
  }, [enabled]);

  return loading;
}

function Skel({ className = '' }: { className?: string }) {
  return <span className={`ac-skel ${className}`} />;
}

function ProfileSkeleton() {
  return (
    <div className="ac-card ac-hero" data-testid="skeleton-profile" aria-hidden="true">
      <div className="ac-profile__top">
        <Skel className="ac-skel--avatar" />
        <div className="ac-skel__stack">
          <Skel className="ac-skel--title" />
          <Skel className="ac-skel--line" />
          <Skel className="ac-skel--short" />
        </div>
      </div>
      <Skel className="ac-skel--art" />
    </div>
  );
}

function StatusSkeleton() {
  return (
    <div className="ac-card" data-testid="skeleton-status" aria-hidden="true">
      <div className="ac-profile__top">
        <Skel className="ac-skel--ico" />
        <div className="ac-skel__stack">
          <Skel className="ac-skel--title" />
          <Skel className="ac-skel--line" />
        </div>
        <Skel className="ac-skel--btn" />
      </div>
    </div>
  );
}

function PremiumSkeleton() {
  return (
    <div className="ac-card" data-testid="skeleton-premium" aria-hidden="true">
      <Skel className="ac-skel--art" />
      <div className="ac-skel__stack">
        <Skel className="ac-skel--title" />
        <Skel className="ac-skel--line" />
        <Skel className="ac-skel--btn" />
      </div>
    </div>
  );
}

function CoinsSkeleton() {
  return (
    <div className="ac-card" data-testid="skeleton-coins" aria-hidden="true">
      <div className="ac-profile__top">
        <Skel className="ac-skel--ico" />
        <div className="ac-skel__stack">
          <Skel className="ac-skel--title" />
          <Skel className="ac-skel--short" />
          <Skel className="ac-skel--line" />
        </div>
      </div>
      <div className="ac-skel__actions">
        <Skel className="ac-skel--action" />
        <Skel className="ac-skel--action" />
        <Skel className="ac-skel--action" />
      </div>
    </div>
  );
}

function DetailsSkeleton() {
  return (
    <div className="ac-card" data-testid="skeleton-details" aria-hidden="true">
      <Skel className="ac-skel--title" />
      {[0, 1, 2, 3].map((key) => (
        <Skel key={key} className="ac-skel--row" />
      ))}
    </div>
  );
}

function LinksSkeleton() {
  return (
    <div className="ac-card" data-testid="skeleton-links" aria-hidden="true">
      <Skel className="ac-skel--title" />
      {[0, 1, 2].map((key) => (
        <Skel key={key} className="ac-skel--row" />
      ))}
    </div>
  );
}

const AccountView: React.FC<AccountViewProps> = ({
  fullName,
  email,
  phone,
  loginType,
  playerId,
  account,
  loading: loadingProp,
  onSignOut,
}) => {
  const autoLoading = usePreviewLoading(loadingProp === undefined);
  const loading = loadingProp ?? autoLoading;
  const snapshot = account ?? SAMPLE_ACCOUNT;
  const titleId = useId();
  const [dialog, setDialog] = useState<AccountDialog | null>(null);
  const [notice, setNotice] = useState('');

  const name = fullName.trim() || 'Account';
  const premium = snapshot.premium;
  const coins = safeCount(snapshot.coins);
  const devices = deviceCountLabel(snapshot.deviceCount);
  const google = isGoogleLogin(loginType);
  const idValue = playerId.trim();

  useEffect(() => {
    if (!notice) return undefined;
    const id = window.setTimeout(() => setNotice(''), 2500);
    return () => window.clearTimeout(id);
  }, [notice]);

  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDialog(null);
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [dialog]);

  const copyId = async () => {
    if (!idValue) return;
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('clipboard');
      }
      await navigator.clipboard.writeText(idValue);
      setNotice('Player ID copied');
    } catch {
      setNotice('Could not copy the player ID');
    }
  };

  const links = [
    {
      to: '/app/devices',
      title: 'Manage devices',
      text: devices,
      icon: Smartphone,
    },
    {
      to: '/app/coin-usage',
      title: 'Coin usage',
      text: 'See where coins were spent',
      icon: BarChart3,
    },
    {
      to: '/app/queries',
      title: 'Help & support',
      text: 'Tickets and replies',
      icon: CircleHelp,
    },
    {
      to: '/app/whats-new',
      title: "What's new",
      text: 'Recent app changes',
      icon: Sparkles,
    },
  ];

  return (
    <section className="ac" aria-busy={loading || undefined}>
      {loading ? (
        <>
          <p className="visually-hidden" role="status">
            Loading account
          </p>
          <ProfileSkeleton />
          <StatusSkeleton />
          <PremiumSkeleton />
          <CoinsSkeleton />
          <DetailsSkeleton />
          <LinksSkeleton />
        </>
      ) : (
        <>
          <section className="ac-card ac-hero">
            <div className="ac-hero__main">
              <div className="ac-profile__top">
                <span className={`ac-avatar${premium ? ' is-premium' : ''}`}>
                  {accountInitials(name)}
                  {premium && (
                    <span className="ac-crown" aria-hidden="true">
                      <Crown size={12} />
                    </span>
                  )}
                </span>
                <div className="ac-profile__copy">
                  <h2>{name}</h2>
                  <p>{email.trim() || 'Email not added'}</p>
                  {premium && (
                    <p className="ac-sync">
                      {google ? <span className="ac-g" aria-hidden="true">G</span> : null}
                      {signInShort(loginType)}
                    </p>
                  )}
                </div>
                <button type="button" className="ac-signout" onClick={() => setDialog('signout')}>
                  <LogOut size={16} aria-hidden="true" />
                  Sign out
                </button>
              </div>

              {!premium && (
                <div className="ac-status">
                  <span className="ac-status__icon" aria-hidden="true">
                    <ShieldCheck size={16} />
                  </span>
                  <div>
                    <p className="ac-status__title">{signInTitle(loginType)}</p>
                    <p>{SYNCED_DESC}</p>
                  </div>
                  <a className="ac-manage" href="#account-details">
                    Manage account
                  </a>
                </div>
              )}
            </div>
            <AccountArt />
          </section>

          {notice && (
            <p className="ac-toast" role="status">
              {notice}
            </p>
          )}

          {premium ? (
            <section className="ac-card ac-plan">
              <div className="ac-plan__member">
                <Crown size={28} aria-hidden="true" />
                <div>
                  <h2>Premium Member</h2>
                  {snapshot.earlyAccess && (
                    <p className="ac-pill">
                      <Users size={13} aria-hidden="true" />
                      Early Community Access
                    </p>
                  )}
                </div>
              </div>
              <span className="ac-plan__split" aria-hidden="true" />
              <button type="button" className="ac-valid" onClick={() => setDialog('plan')}>
                <span className="ac-valid__icon" aria-hidden="true">
                  <Calendar size={16} />
                </span>
                <span>
                  <span className="ac-valid__label">Valid till</span>
                  <strong>{snapshot.validTill}</strong>
                </span>
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </section>
          ) : (
            <section className="ac-card ac-teaser">
              <PremiumArt />
              <div className="ac-teaser__copy">
                <h2>{PREMIUM_OFF_TITLE}</h2>
                <p>{PREMIUM_OFF_BODY}</p>
                <button type="button" className="ac-go" onClick={() => setDialog('plan')}>
                  <Crown size={16} aria-hidden="true" />
                  Go Premium
                </button>
              </div>
            </section>
          )}

          {!premium && (
            <section className="ac-card ac-coins">
              <div className="ac-coins__top">
                <CoinsArt />
                <div>
                  <div className="ac-coins__title">
                    <h2>Mock Coins</h2>
                    <button type="button" className="ac-info" aria-label="Mock Coins info" onClick={() => setDialog('coins')}>
                      <Info size={16} aria-hidden="true" />
                    </button>
                  </div>
                  <p className="ac-coins__balance">{coins}</p>
                  <p>{COINS_DESC}</p>
                </div>
              </div>
              <div className="ac-coins__actions">
                <button type="button" onClick={() => setDialog('coins')}>
                  <CircleHelp size={16} aria-hidden="true" />
                  How it works
                </button>
                <button type="button" onClick={() => setDialog('earn')}>
                  <Sparkles size={16} aria-hidden="true" />
                  Earn in the app
                </button>
                <Link to="/app/coin-usage">
                  <BarChart3 size={16} aria-hidden="true" />
                  Usage info
                </Link>
              </div>
            </section>
          )}

          <section className="ac-card ac-details" id="account-details">
            <h2>Account details</h2>
            <dl>
              <div className="ac-details__row">
                <dt>Email</dt>
                <dd>{email.trim() || 'Not added'}</dd>
              </div>
              <div className="ac-details__row">
                <dt>Phone</dt>
                <dd>{phone.trim() || 'Not added'}</dd>
              </div>
              <div className="ac-details__row">
                <dt>Sign-in</dt>
                <dd>{signInTitle(loginType)}</dd>
              </div>
              <div className="ac-details__row">
                <dt>Player ID</dt>
                <dd className="ac-id">{idValue || 'Not available'}</dd>
                <button type="button" className="ac-copy" onClick={copyId} disabled={!idValue}>
                  <Copy size={14} aria-hidden="true" />
                  Copy
                </button>
              </div>
              <div className="ac-details__row">
                <dt>Member since</dt>
                <dd>{snapshot.memberSince}</dd>
              </div>
            </dl>
          </section>

          <section className="ac-card">
            <h2>On this account</h2>
            <ul className="ac-links">
              {links.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <Link className="ac-link" to={item.to}>
                      <span className="ac-link__icon" aria-hidden="true">
                        <Icon size={18} />
                      </span>
                      <span>
                        <span className="ac-link__title">{item.title}</span>
                        <small>{item.text}</small>
                      </span>
                      <ChevronRight size={18} aria-hidden="true" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}

      {dialog && (
        <div className="ac-modal" onMouseDown={() => setDialog(null)}>
          <div
            className="ac-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <span className="ac-dialog__handle" aria-hidden="true" />
            {dialog === 'plan' && <PremiumArt />}
            <h2 id={titleId}>
              {dialog === 'plan' && (premium ? 'About this plan' : 'Go Premium')}
              {dialog === 'coins' && 'How mock coins work'}
              {dialog === 'earn' && 'Earn coins in the Android app'}
              {dialog === 'signout' && 'Sign out'}
            </h2>
            {dialog === 'plan' && premium && <p className="ac-dialog__detail">Valid till {snapshot.validTill}</p>}
            <p>
              {dialog === 'plan' && (premium ? PREMIUM_ON_DIALOG : PREMIUM_OFF_DIALOG)}
              {dialog === 'coins' && `${COINS_PLAY_DESC} ${COINS_DESC}`}
              {dialog === 'earn' && EARN_DESC}
              {dialog === 'signout' && SIGNOUT_DESC}
            </p>
            <div className="ac-dialog__actions">
              {dialog === 'signout' ? (
                <>
                  <button type="button" className="ac-ghost" onClick={() => setDialog(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ac-danger"
                    onClick={() => {
                      setDialog(null);
                      onSignOut();
                    }}
                  >
                    Sign out now
                  </button>
                </>
              ) : (
                <button type="button" className="ac-primary" onClick={() => setDialog(null)}>
                  Got it
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AccountView;
